import { useNavigate } from 'react-router-dom'
import { Plus, Building2, CheckCircle2, WifiOff, Clock, AlertCircle, RefreshCw, Settings2, ChevronRight } from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useAdConfigs, useTestAdConnection } from '../../hooks/useAdDomains'
import { formatDateTime } from '../../utils/format'

export default function Domains() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: configs, isLoading, error } = useAdConfigs()
  const testMutation = useTestAdConnection()

  const STATUS_CONFIG = {
    Online:  { labelKey: 'common.status.online',      cls: 'badge-success', Icon: CheckCircle2 },
    Offline: { labelKey: 'common.status.offline',     cls: 'badge-gray',    Icon: WifiOff      },
    Pending: { labelKey: 'common.status.pending',     cls: 'badge-warning', Icon: Clock        },
    Error:   { labelKey: 'common.status.error',       cls: 'badge-error',   Icon: AlertCircle  },
  }

  const handleTest = async (e, d) => {
    e.stopPropagation()
    try { await testMutation.mutateAsync(d.id) }
    catch (err) { alert(t('domains.list.loadError') + ': ' + err.message) }
  }

  if (isLoading) return (
    <div className="flex items-center justify-center h-64 text-gray-500">{t('domains.list.loading')}</div>
  )

  if (error) return (
    <div className="flex items-center justify-center h-64 text-red-600 gap-2">
      <AlertCircle className="w-5 h-5" /> {t('domains.list.loadError')}: {error.message}
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('domains.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">{t('domains.subtitle')}</p>
        </div>
        <button onClick={() => navigate('/domains/new')} className="btn btn-primary">
          <Plus className="w-4 h-4" />
          {t('domains.new')}
        </button>
      </div>

      {configs?.length === 0 ? (
        <div className="card p-12 text-center">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">{t('domains.empty.none')}</h3>
          <p className="text-sm text-gray-500 mb-6">
            {t('domains.empty.firstHint')}
          </p>
          <button onClick={() => navigate('/domains/new')} className="btn btn-primary mx-auto">
            <Plus className="w-4 h-4" />
            {t('domains.new')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {configs?.map((d) => {
            const status = STATUS_CONFIG[d.connectionStatus] ?? STATUS_CONFIG.Pending
            const StatusIcon = status.Icon
            return (
              <div
                key={d.id}
                className="card p-5 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/domains/${d.id}`)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div
                      className="icon-box flex-shrink-0"
                      style={{ background: 'var(--theme-icon-box-background)', color: 'var(--theme-icon-box-text)' }}
                    >
                      <Building2 className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-semibold text-gray-900">{d.displayName}</h3>
                        {!d.enabled && <span className="badge badge-gray text-xs">{t('users.status.disabled')}</span>}
                        <span className={clsx('badge', status.cls, 'flex items-center gap-1')}>
                          <StatusIcon className="w-3 h-3" />
                          {t(status.labelKey)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-sm text-gray-500 font-mono">{d.server}:{d.port}{d.useSsl ? ' (SSL)' : ''}</span>
                        <span className="text-xs text-gray-400">{d.baseDn}</span>
                      </div>
                      {d.lastConnectionError && (
                        <p className="text-xs text-red-600 mt-1">{d.lastConnectionError}</p>
                      )}
                      {d.lastConnectionTest && (
                        <p className="text-xs text-gray-400 mt-1">
                          {t('domains.lastTest')}: {formatDateTime(d.lastConnectionTest)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={(e) => handleTest(e, d)}
                      disabled={testMutation.isPending}
                      className="btn btn-secondary text-sm flex items-center gap-1"
                      title={t('domains.testConnectionNow')}
                    >
                      <RefreshCw className={clsx('w-4 h-4', testMutation.isPending && 'animate-spin')} />
                      {t('domains.test')}
                    </button>
                    <button
                      onClick={() => navigate(`/domains/${d.id}`)}
                      className="btn btn-primary text-sm flex items-center gap-1"
                    >
                      <Settings2 className="w-4 h-4" />
                      {t('domains.manage')}
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
